/**
 * Small pure selectors over the role-filtered {@link GameState} the client holds.
 * Keep view components dumb: they read derived values from here.
 */

import {
  dailyDoubleWagerBounds,
  finalJeopardyWagerBounds,
  nextFinalJudgePlayerId,
  type Clue,
  type ClueReveal,
  type GameState,
  type Player,
  type WagerBounds,
  gamePhaseOf,
} from '@jeopardy/shared';

/** The currently open clue (from `clueState`), or undefined. */
export function currentClue(state: GameState | null): Clue | undefined {
  if (!state?.jeopardy.clueState) return undefined;
  return state.jeopardy.packSnapshot.cluesById[state.jeopardy.clueState.clueId];
}

/** The category name for a clue id, or undefined. */
export function categoryNameForClue(
  state: GameState | null,
  clueId: string,
): string | undefined {
  if (!state) return undefined;
  const clue = state.jeopardy.packSnapshot.cluesById[clueId];
  if (!clue) return undefined;
  const cat = state.jeopardy.board.categories.find((c) => c.id === clue.categoryId);
  return cat?.name;
}

/** True when a clue is open (phase CLUE_OPEN with a clueState). */
export function isClueOpen(state: GameState | null): boolean {
  return state?.jeopardy.phase === 'CLUE_OPEN' && state.jeopardy.clueState !== undefined;
}

/**
 * The end-of-clue reveal payload (#192, FR-4.10) when the open clue is holding in
 * `RESOLVED`, else null. Non-null → render the reveal band / award cue instead of
 * the buzzer, and disable buzzing (the clue is resolved).
 */
export function clueReveal(state: GameState | null): ClueReveal | null {
  if (state?.jeopardy.phase !== 'CLUE_OPEN') return null;
  const cs = state.jeopardy.clueState;
  if (!cs || cs.status !== 'RESOLVED' || !cs.reveal) return null;
  return cs.reveal;
}

/**
 * The correct response to display during an UNANSWERED reveal (#192), or null. On
 * the unanswered path the server un-blanks the resolved clue's response for all
 * roles (see game-server `views.ts`); on a correct-award reveal it stays blank (the
 * answer is never shown), so this returns null there.
 */
export function revealedResponse(state: GameState | null): string | null {
  const reveal = clueReveal(state);
  if (!reveal || reveal.outcome !== 'unanswered') return null;
  const clue = currentClue(state);
  const response = clue?.response ?? '';
  return response.length > 0 ? response : null;
}

/**
 * The Final Peril correct response to display, or null (#205, FR-6.6). Non-null
 * while `phase ∈ {FINAL_JUDGING, FINAL_REVEAL}`, where the server un-blanks the Final
 * `response` for all roles (see game-server `views.ts`) — shown throughout the
 * contestant walk (DI-A(b)) and the post-judging linger. Null everywhere else
 * (blanked before judging and after the game ends; GAME_OVER instead reads
 * `finalOutcome`).
 */
export function finalRevealResponse(state: GameState | null): string | null {
  if (
    !state ||
    (gamePhaseOf(state) !== 'FINAL_JUDGING' && gamePhaseOf(state) !== 'FINAL_REVEAL')
  ) {
    return null;
  }
  const response = state.jeopardy.packSnapshot.finalJeopardy.response ?? '';
  return response.length > 0 ? response : null;
}

/**
 * Epoch ms at which the Final Peril linger ends and the game advances to GAME_OVER
 * (#205, FR-6.6), or null unless `phase === 'FINAL_REVEAL'`. Drives the "final
 * results in {n}s" countdown; recovery re-arms the remaining slice server-side.
 */
export function finalRevealResolvesAt(state: GameState | null): number | null {
  if (!state || gamePhaseOf(state) !== 'FINAL_REVEAL') return null;
  return state.jeopardy.final?.reveal?.resolvesAtEpochMs ?? null;
}

/**
 * True when the game is in **active play** — any phase past the pre-game lobby and
 * before it's over (i.e. not LOBBY/GENERATING/GAME_OVER). Drives the safe-exit
 * confirm (FR-9.8 / D-F8.2): leaving mid-play prompts; leaving from the lobby or the
 * game-over screen does not.
 */
export function isActivePlayPhase(state: GameState | null): boolean {
  if (!state) return false;
  return (
    gamePhaseOf(state) !== 'LOBBY' &&
    gamePhaseOf(state) !== 'GENERATING' &&
    gamePhaseOf(state) !== 'GAME_OVER'
  );
}

/** True when the open clue is a Daily Double still awaiting its wager. */
export function isDailyDoubleAwaitingWager(state: GameState | null): boolean {
  return (
    state?.jeopardy.phase === 'CLUE_OPEN' &&
    state.jeopardy.clueState?.mode === 'DAILY_DOUBLE' &&
    state.jeopardy.clueState.status === 'AWAITING_WAGER'
  );
}

/**
 * If `playerId` is the Daily Double uncoverer who must still wager, return their
 * server-computed wager bounds; else null. The player's wager input renders only
 * when this is non-null.
 */
export function myDailyDoubleWager(
  state: GameState | null,
  playerId: string | null,
): WagerBounds | null {
  if (!state || playerId === null) return null;
  const clueState = state.jeopardy.clueState;
  if (
    gamePhaseOf(state) !== 'CLUE_OPEN' ||
    clueState?.mode !== 'DAILY_DOUBLE' ||
    clueState.status !== 'AWAITING_WAGER' ||
    clueState.dailyDoublePlayerId !== playerId
  ) {
    return null;
  }
  const me = state.players.find((p) => p.id === playerId);
  if (!me) return null;
  return dailyDoubleWagerBounds(state.rules, me.score);
}

/**
 * If this player must still submit a Final Jeopardy wager, return their bounds;
 * else null. Non-null → show the Final wager input (FR-6.2).
 */
export function myFinalWager(
  state: GameState | null,
  playerId: string | null,
): WagerBounds | null {
  if (
    !state ||
    playerId === null ||
    gamePhaseOf(state) !== 'FINAL_WAGER' ||
    !state.jeopardy.final
  ) {
    return null;
  }
  if (playerId in state.jeopardy.final.wagers) return null; // already wagered
  const me = state.players.find((p) => p.id === playerId);
  if (!me) return null;
  return finalJeopardyWagerBounds(state.rules, me.score);
}

/**
 * True when this player should type their secret Final answer now (FINAL_CLUE,
 * they wagered, and haven't answered yet) — FR-6.3.
 */
export function shouldTypeFinalAnswer(
  state: GameState | null,
  playerId: string | null,
): boolean {
  if (
    !state ||
    playerId === null ||
    gamePhaseOf(state) !== 'FINAL_CLUE' ||
    !state.jeopardy.final
  ) {
    return false;
  }
  return (
    playerId in state.jeopardy.final.wagers && !(playerId in state.jeopardy.final.answers)
  );
}

/** The player the host must judge next in Final Jeopardy, or null. */
export function nextFinalJudgePlayer(state: GameState | null): Player | null {
  if (!state || gamePhaseOf(state) !== 'FINAL_JUDGING' || !state.jeopardy.final)
    return null;
  const nextId = nextFinalJudgePlayerId(
    state.jeopardy.final.judgingOrder,
    state.jeopardy.final.judgedPlayerIds,
  );
  if (nextId === null) return null;
  return state.players.find((p) => p.id === nextId) ?? null;
}

/**
 * The currently-rated contestant's name + submitted answer to show the room during
 * Final Peril judging (#344, FR-6.7), or null outside `FINAL_JUDGING` / when everyone has
 * been judged. The server reveals only this one contestant's answer to non-host roles (see
 * game-server `views.ts`); an absent submission yields an empty string (the band renders the
 * `(no answer submitted)` placeholder). The wager is intentionally NOT included — it stays
 * hidden until the host judges this contestant (FR-6.4).
 */
export function finalJudgingAnswer(
  state: GameState | null,
): { name: string; answer: string } | null {
  const player = nextFinalJudgePlayer(state);
  if (!player || !state?.jeopardy.final) return null;
  return { name: player.name, answer: state.jeopardy.final.answers[player.id] ?? '' };
}
